import { authenticatedFetch } from '../JWTAuthService';
import { configuration } from '../Configuration';
import { ScaleCategory } from './MetricsAPI';

/**
 * INKAR indicators (`GET /map-layers/inkar/catalog`, `POST /map-layers/inkar`).
 *
 * INKAR is collected per administrative unit — a municipality, a district, a
 * state — and holds for the whole of it. So an indicator is not drawn as a grid
 * but as the outlines of the units the area touches, each coloured by its own
 * value and naming it on hover.
 *
 * The catalogue is derived on the server from the model's own fields, so nothing
 * about the 336 indicators is written down here.
 */

const baseUrl = configuration.serverUrl;

export interface InkarIndicator {
    name: string;
    label: { de: string; en: string };
}

export interface InkarGroup {
    type: string;
    label: { de: string; en: string };
    fields: InkarIndicator[];
}

export interface InkarSelection {
    type: string;
    field: string;
    /** Kept so the selection can be named without the catalogue at hand. */
    label: string;
    groupLabel: string;
}

export interface InkarResult {
    type: string;
    field: string;
    label: string;
    /**
     * The administrative levels the figures are per, finest first. More than one
     * where a district had to stand in for municipalities that carry no figures.
     */
    levels: { level: string; count: number }[];
    year: number | null;
    unitCount: number;
    scale: ScaleCategory[];
    elements: any[];
    statistics: { min: number; max: number; mean: number } | null;
    /** The area held more units, or more years, than one answer carries. */
    truncated: boolean;
}

export const getInkarCatalog = async (): Promise<InkarGroup[]> => {
    const response = await authenticatedFetch(`${baseUrl}/map-layers/inkar/catalog`, { method: 'GET' });

    if (!response || !response.ok) {
        throw new Error(`INKAR-Katalog konnte nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }

    const payload = await response.json();
    return payload?.groups ?? [];
};

export const loadInkarIndicator = async (
    geometry: any,
    selection: InkarSelection,
    locale: string,
): Promise<InkarResult> => {
    const response = await authenticatedFetch(`${baseUrl}/map-layers/inkar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            geometry,
            type: selection.type,
            field: selection.field,
            locale: locale.startsWith('de') ? 'de' : 'en',
        }),
    });

    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(
            detail?.error ?? `Indikator konnte nicht geladen werden (${response?.status ?? 'keine Antwort'})`,
        );
    }

    return response.json();
};

/** What the administrative levels are called in the caption. */
const levelName = (level: string | null, locale: string): string => {
    const de: Record<string, string> = {
        Municipality: 'Gemeinden',
        City: 'Städte',
        County: 'Landkreise',
        AdministrativeDistrict: 'Verwaltungsbezirke',
        GovernmentDistrict: 'Regierungsbezirke',
        State: 'Bundesländer',
        Country: 'Staaten',
    };
    const en: Record<string, string> = {
        Municipality: 'municipalities',
        City: 'cities',
        County: 'districts',
        AdministrativeDistrict: 'administrative districts',
        GovernmentDistrict: 'government districts',
        State: 'states',
        Country: 'countries',
    };

    if (!level) return '';
    return (locale.startsWith('de') ? de : en)[level] ?? level;
};

/**
 * The levels named for a caption: "42 Gemeinden, 2 Landkreise".
 *
 * Each with its own count, because "44 units" over two levels says less than
 * nothing — it invites the reader to compare a municipality's figure with a
 * district's as though they were the same kind of measurement.
 */
export const levelSummary = (levels: { level: string; count: number }[], locale: string): string =>
    levels.map((entry) => `${entry.count} ${levelName(entry.level, locale)}`).join(', ');

/** Just the level names, for the legend's foot. */
export const levelNames = (levels: { level: string; count: number }[], locale: string): string =>
    levels.map((entry) => levelName(entry.level, locale)).join(', ');
